import React, { useState, useEffect } from "react";
import { Typography, Card, CardContent, Box, Grid } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PeopleIcon from '@mui/icons-material/People';
import { getAllUsers, getAllSites } from '../api/apiService';

// Premium KPI card with subtle tinted background and colored shadows
const StyledCard = styled(Card)(({ theme, $accent }) => ({
  backgroundColor: theme.palette.background.paper,
  backgroundImage: $accent
    ? `linear-gradient(180deg, ${alpha($accent, theme.palette.mode === 'dark' ? 0.08 : 0.04)} 0%, rgba(255,255,255,0) 40%)`
    : 'none',
  borderRadius: '16px',
  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
  boxShadow: $accent
    ? `0 6px 18px ${alpha($accent, theme.palette.mode === 'dark' ? 0.28 : 0.18)}`
    : theme.palette.mode === 'dark'
      ? '0 6px 18px rgba(0,0,0,0.35)'
      : '0 4px 12px rgba(0,0,0,0.06)',
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: $accent
      ? `0 10px 28px ${alpha($accent, theme.palette.mode === 'dark' ? 0.36 : 0.28)}`
      : theme.palette.mode === 'dark'
        ? '0 10px 28px rgba(0,0,0,0.45)'
        : '0 8px 24px rgba(0,0,0,0.10)',
    borderColor: $accent ? alpha($accent, theme.palette.mode === 'dark' ? 0.32 : 0.24) : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)')
  }
}));

function RecentDataCard() {
  const [userCount, setUserCount] = useState(0);
  const [siteStats, setSiteStats] = useState({ total: 0, completed: 0, ongoing: 0 });

  useEffect(() => {
    const fetchUsersCount = async () => {
      try {
        console.log('[RecentDataCard] Fetching users count...');
        const users = await getAllUsers();
        console.log('[RecentDataCard] Users fetched:', users);
        setUserCount(users.length || 0);
      } catch (error) {
        console.error('[RecentDataCard] Error fetching users count:', error);
        console.error('[RecentDataCard] Error details:', error.message, error.stack);
      }
    };
    fetchUsersCount();
  }, []);

  useEffect(() => {
    const fetchSiteStats = async () => {
      try {
        console.log('[RecentDataCard] Fetching site stats...');
        const sites = await getAllSites();
        console.log('[RecentDataCard] Sites fetched:', sites);
        const total = sites.length;
        // Completed card = Active; Ongoing card = Ongoing
        const completed = sites.filter((site) => site.status === 'Active').length;
        const ongoing = sites.filter((site) => site.status === 'Ongoing').length;
        setSiteStats({ total, completed, ongoing });
        console.log('[RecentDataCard] Site stats calculated:', { total, completed, ongoing });
      } catch (error) {
        console.error('[RecentDataCard] Error fetching site stats:', error);
        console.error('[RecentDataCard] Error details:', error.message, error.stack);
      }
    };
    fetchSiteStats();
  }, []);

  const cards = [
    {
      label: 'Total Sites',
      value: siteStats.total,
      icon: LocationOnIcon,
      color: '#0A84FF',
      gradient: ''
    },
    {
      label: 'Completed',
      value: siteStats.completed,
      icon: CheckCircleIcon,
      color: '#32D74B',
      gradient: ''
    },
    {
      label: 'Ongoing',
      value: siteStats.ongoing,
      icon: PendingIcon,
      color: '#FFD60A',
      gradient: ''
    },
    {
      label: 'Total Users',
      value: userCount,
      icon: PeopleIcon,
      color: '#6C5CE7',
      gradient: ''
    }
  ];

  return (
    <Box sx={{ 
      flexGrow: 1, 
      padding: 2,
      marginBottom: 2,
      overflowX: 'auto',
    }}>
      <Grid container spacing={2} sx={{ width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        {cards.map(({ label, value, icon: Icon, color, gradient }, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <StyledCard $accent={color}>
              <CardContent sx={{ 
                padding: 2,
                position: "relative",
                zIndex: 1,
              }}>
                <Typography 
                  variant="h5"
                  className="value-text"
                  sx={{ 
                    fontWeight: 700,
                    color: (theme) => theme.palette.text.primary,
                    marginBottom: 1,
                    letterSpacing: "-0.5px",
                    transition: "color 0.3s ease-in-out",
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  }}
                >
                  {value}
                </Typography>
                <Typography 
                  variant="body2"
                  component="div"
                  sx={{ 
                    color: (theme) => color === '#FFD60A' ? (theme.palette.mode === 'dark' ? '#F1C21B' : '#332B00') : theme.palette.text.secondary,
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  <Box sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    backgroundColor: color === '#FFD60A' ? 'rgba(255,214,10,0.18)' : (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon sx={{ 
                      color: color === '#FFD60A' ? '#B45309' : color,
                      fontSize: { xs: 16, sm: 18 },
                    }} />
                  </Box>
                  {label}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default RecentDataCard;
